import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import contactTeacherImage from '../assets/images/contact-teacher.png';
import emailIconImage from '../assets/images/email_icon.png';
import chatIconImage from '../assets/images/chat_icon.png';

// Contact Icon Component with fallback and circular background
const ContactIcon: React.FC<{ src: string; emoji: string; alt?: string; bgColor?: string }> = ({ src, emoji, alt = '', bgColor = '#a8d5e2' }) => {
    const [imgError, setImgError] = useState(false);
    
    return (
        <div className="contact-icon-wrapper" style={{ backgroundColor: bgColor }}>
            {imgError ? (
                <span className="contact-icon-emoji">{emoji}</span>
            ) : (
                <img 
                    src={src} 
                    loading="lazy" 
                    alt={alt} 
                    className="contact-icon"
                    onError={() => setImgError(true)}
                />
            )}
        </div>
    );
};

const ContactUs: React.FC = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');

    // Auto-hide success/error message after 10 seconds
    useEffect(() => {
        if (formStatus === 'success' || formStatus === 'error') {
            const timer = setTimeout(() => {
                setFormStatus('idle');
            }, 6000);
            return () => clearTimeout(timer);
        }
    }, [formStatus]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFormStatus('idle');

        try {
            // Call the contact submission API
            const response = await fetch('/web/contact/submit', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Failed to submit form');
            }

            // Success
            setFormStatus('success');
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            });
        } catch (error) {
            console.error('Error submitting contact form:', error);
            setFormStatus('error');
        }
    };

    return (
        <div id="contact" className="section-contact-us">
            <div className="padding-global">
                <div className="container-large">
                    <div className="padding-section-large">
                        <div className="contact-us-component">
                            {/* Two Column Layout */}
                            <div className="contact-us-layout">
                                {/* Left Column - Contact Info */}
                                <div className="contact-us-left-content">
                                    <div className="contact-us-top-content">
                                        <div className="heading-wrapper">
                                            <h2 className="heading-style-h2">{t('contactUs.contactTitle')}</h2>
                                            <div className="line-color-green" style={{ width: '100%', height: '15px' }}></div>
                                        </div>
                                        <div className="contact-info-image-wrapper">
                                            <img 
                                                src={contactTeacherImage} 
                                                alt="Contact us" 
                                                className="contact-info-image"
                                            />
                                        </div>
                                        <div className="padding-bottom padding-xxsmall"></div>
                                        <p className="text-size-regular">{t('contactUs.subtitle')}</p>
                                    </div>
                                    <div className="contact-details">
                                        <a href={`mailto:${t('contactUs.emailValue')}`} className="contact-address-wapper _02 w-inline-block">
                                            <ContactIcon 
                                                src={emailIconImage} 
                                                emoji="✉️"
                                                bgColor="#fccdda"
                                            />
                                            <div className="contact-address">
                                                <h6 className="heading-style-h6">{t('contactUs.emailAddress')}</h6>
                                                <h5 className="heading-style-h5">{t('contactUs.emailValue')}</h5>
                                            </div>
                                        </a>
                                        <div className="contact-address-wapper _01">
                                            <ContactIcon 
                                                src={chatIconImage} 
                                                emoji="⏰"
                                                bgColor="#ffe3ac"
                                            />
                                            <div className="contact-address">
                                                <h6 className="heading-style-h6">{t('contactUs.responseTime')}</h6>
                                                <h5 className="heading-style-h5">{t('contactUs.responseTimeValue')}</h5>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column - Form */}
                                <div className="contact-us-form-column">
                                    <div className="contact-us-form-block">
                                        <h2 className="contact-form-title">{t('contactUs.formTitle')}</h2>
                                        <form 
                                            id="email-form" 
                                            name="email-form" 
                                            data-name="Email Form" 
                                            method="post" 
                                            className="contact-us-form" 
                                            onSubmit={handleSubmit}
                                            aria-label="Email Form"
                                        >
                                            <div className="w-layout-grid contact-grid">
                                                <div className="contact-field-wrapper">
                                                    <label htmlFor="name" className="text-saize-bold">{t('contactUs.fullName')} <span className="text-color-pink">*</span></label>
                                                    <input
                                                        className="input w-input"
                                                        maxLength={256}
                                                        name="name"
                                                        data-name="Name"
                                                        placeholder={t('contactUs.fullNamePlaceholder')}
                                                        type="text"
                                                        id="name"
                                                        required
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                    />
                                                </div>

                                                <div className="contact-field-wrapper">
                                                    <label htmlFor="Email" className="text-saize-bold">{t('contactUs.emailAddressLabel')} <span className="text-color-pink">*</span></label>
                                                    <input
                                                        className="input w-input"
                                                        maxLength={256}
                                                        name="email"
                                                        data-name="Email"
                                                        placeholder={t('contactUs.emailPlaceholder')}
                                                        type="email"
                                                        id="Email"
                                                        required
                                                        value={formData.email}
                                                        onChange={handleInputChange}
                                                    />
                                                </div>

                                                <div className="contact-field-wrapper">
                                                    <label htmlFor="Phone-Number" className="text-saize-bold">{t('contactUs.phoneNumber')}</label>
                                                    <input
                                                        className="input w-input"
                                                        maxLength={256}
                                                        name="phone"
                                                        data-name="Phone Number"
                                                        placeholder={t('contactUs.phonePlaceholder')}
                                                        type="tel"
                                                        id="Phone-Number"
                                                        value={formData.phone}
                                                        onChange={handleInputChange}
                                                    />
                                                </div>

                                                <div className="contact-field-wrapper">
                                                    <label htmlFor="subject" className="text-saize-bold">{t('contactUs.subject')} <span className="text-color-pink">*</span></label>
                                                    <select
                                                        className="input w-input w-select"
                                                        name="subject"
                                                        data-name="subject"
                                                        id="subject"
                                                        required
                                                        value={formData.subject}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">{t('contactUs.subjectOptions.selectSubject')}</option>
                                                        <option value="general">{t('contactUs.subjectOptions.generalInquiry')}</option>
                                                        <option value="technical">{t('contactUs.subjectOptions.technicalSupport')}</option>
                                                        <option value="billing">{t('contactUs.subjectOptions.billingQuestion')}</option>
                                                        <option value="feature">{t('contactUs.subjectOptions.featureRequest')}</option>
                                                        <option value="partnership">{t('contactUs.subjectOptions.partnership')}</option>
                                                        <option value="other">{t('contactUs.subjectOptions.other')}</option>
                                                    </select>
                                                </div>

                                                <div className="contact-field-wrapper full-width">
                                                    <label htmlFor="Message" className="text-saize-bold">{t('contactUs.message')} <span className="text-color-pink">*</span></label>
                                                    <textarea
                                                        required
                                                        placeholder={t('contactUs.messagePlaceholder')}
                                                        maxLength={5000}
                                                        id="Message"
                                                        name="message"
                                                        data-name="Message"
                                                        className="input message-field w-input"
                                                        value={formData.message}
                                                        onChange={handleInputChange}
                                                    ></textarea>
                                                </div>
                                            </div>

                                            <input
                                                type="submit"
                                                data-wait="Please wait..."
                                                className="button contact-submit-button w-button"
                                                value={t('contactUs.sendButton')}
                                            />
                                        </form>
                                        
                                        {formStatus === 'success' && (
                                            <div className="success-message w-form-done" tabIndex={-1} role="region" aria-label="Email Form success">
                                                <div>{t('contactUs.successMessage')}</div>
                                            </div>
                                        )}

                                        {formStatus === 'error' && (
                                            <div className="error-message w-form-fail" tabIndex={-1} role="region" aria-label="Email Form failure">
                                                <div>{t('contactUs.errorMessage')}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContactUs;

